import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Settings as SettingsIcon,
  Mail,
  User,
  Shield,
  Bell,
  CreditCard,
  RefreshCw,
  CheckCircle2,
  XCircle,
  ExternalLink,
  LogOut,
  Key,
  Trash2,
  AlertCircle,
  Plug
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export default function SettingsPage({ user }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [gmailStatus, setGmailStatus] = useState({ connected: false, email: null });
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    fetchGmailStatus();
    
    // Handle Gmail OAuth callback
    const gmailParam = searchParams.get("gmail");
    if (gmailParam === "success") {
      toast.success("Gmail connected successfully! You can now send emails from your account.");
      fetchGmailStatus();
      // Clean URL
      window.history.replaceState({}, "", "/settings");
    } else if (gmailParam === "error") {
      const message = searchParams.get("message") || "Failed to connect Gmail";
      toast.error(`Gmail connection failed: ${message}`);
      window.history.replaceState({}, "", "/settings");
    }
  }, [searchParams]);

  const fetchGmailStatus = async () => {
    try {
      const res = await api.get("/gmail/status");
      setGmailStatus(res.data);
    } catch (e) {
      console.error("Failed to fetch Gmail status:", e);
    }
  };

  const connectGmail = () => {
    // Redirect to Gmail OAuth
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/gmail/connect`;
  };

  const disconnectGmail = async () => {
    setDisconnecting(true);
    try {
      await api.post("/gmail/disconnect");
      setGmailStatus({ connected: false, email: null });
      toast.success("Gmail disconnected");
      setShowDisconnectDialog(false);
    } catch (e) {
      toast.error("Failed to disconnect Gmail");
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <SettingsIcon className="w-7 h-7 text-indigo-400" />
        <div>
          <h1 className="text-2xl font-bold text-indigo-500">Settings</h1>
          <p className="text-indigo-400">Manage your account and integrations</p>
        </div>
      </div>

      {/* Account Section */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500 flex items-center gap-2">
            <User className="w-5 h-5 text-indigo-400" />
            Account
          </CardTitle>
          <CardDescription className="text-indigo-400">
            Your account information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-indigo-400 text-sm">Name</Label>
              <p className="text-indigo-500 font-medium">{user?.name || "Not set"}</p>
            </div>
            <div>
              <Label className="text-indigo-400 text-sm">Email</Label>
              <p className="text-indigo-500 font-medium">{user?.email || "Not set"}</p>
            </div>
            <div>
              <Label className="text-indigo-400 text-sm">Plan</Label>
              <div className="flex items-center gap-2">
                <Badge className={
                  user?.tier === "growth" ? "bg-indigo-500/20 text-indigo-400" :
                  user?.tier === "pro" ? "bg-indigo-500/20 text-indigo-400" :
                  "bg-slate-400 text-slate-400"
                }>
                  {user?.tier?.charAt(0).toUpperCase() + user?.tier?.slice(1) || "Free"}
                </Badge>
                {user?.is_trial && (
                  <span className="text-xs text-indigo-400">Trial</span>
                )}
              </div>
            </div>
            <div>
              <Label className="text-indigo-400 text-sm">Auth Method</Label>
              <p className="text-indigo-500 font-medium capitalize">{user?.auth_method || "Google"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Integration Section */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500 flex items-center gap-2">
            <Mail className="w-5 h-5 text-green-400" />
            Email Integration
          </CardTitle>
          <CardDescription className="text-indigo-400">
            Connect your Gmail to send emails from your own address
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Gmail Connection */}
          <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${gmailStatus.connected ? "bg-green-500/20" : "bg-slate-400"}`}>
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill={gmailStatus.connected ? "#22c55e" : "#94a3b8"} d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-indigo-500">Gmail</h3>
                  {gmailStatus.connected ? (
                    <p className="text-sm text-green-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Connected as {gmailStatus.email}
                    </p>
                  ) : (
                    <p className="text-sm text-indigo-400">
                      Send emails from your Gmail address
                    </p>
                  )}
                </div>
              </div>
              
              {gmailStatus.connected ? (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowDisconnectDialog(true)}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Disconnect
                </Button>
              ) : (
                <Button 
                  onClick={connectGmail}
                  className="bg-white text-indigo-500 hover:bg-indigo-100"
                  data-testid="connect-gmail-btn"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Connect Gmail
                </Button>
              )}
            </div>
          </div>
          
          {/* Info about Gmail integration */}
          {!gmailStatus.connected && (
            <Alert className="bg-blue-500/10 border-slate-400/30">
              <AlertCircle className="w-4 h-4 text-indigo-400" />
              <AlertDescription className="text-slate-600">
                <strong>Why connect Gmail?</strong> When you connect Gmail, client emails (proposals, contracts, reminders) will be sent from your email address instead of our system email. This builds trust and ensures replies go directly to your inbox.
              </AlertDescription>
            </Alert>
          )}
          
          {gmailStatus.connected && (
            <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/30">
              <h4 className="font-medium text-green-400 mb-2">Gmail is active for:</h4>
              <ul className="text-sm text-slate-400 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-green-400" />
                  Proposal emails to clients
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-green-400" />
                  Contract signing requests
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-green-400" />
                  Scheduled follow-up emails
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-3 h-3 text-green-400" />
                  Cold outreach campaigns
                </li>
              </ul>
            </div>
          )}

          {/* Default email (when Gmail not connected) */}
          <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-indigo-500">Default Sender</h3>
                <p className="text-sm text-indigo-400">
                  {gmailStatus.connected 
                    ? "Fallback when Gmail is unavailable" 
                    : "Used when Gmail is not connected"}
                </p>
              </div>
              <Badge className="bg-slate-400 text-slate-400">
                hello@yourfreelanceros.com
              </Badge>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {gmailStatus.connected 
                ? "If your Gmail token expires, emails will be sent from this address with your email as reply-to."
                : "Emails are sent from this address with your email set as reply-to, so client replies go to you."}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Claude Connector Section */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500 flex items-center gap-2">
            <Plug className="w-5 h-5 text-indigo-400" />
            Claude Connector
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl border border-slate-700">
            <div>
              <h3 className="font-medium text-indigo-500">Manage your freelance business from Claude</h3>
              <p className="text-sm text-indigo-400">Control Read / Write / Act permissions and get connect instructions</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-400"
              onClick={() => navigate("/settings/connector")}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Manage
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security Section */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500 flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-400" />
            Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl border border-slate-700">
            <div>
              <h3 className="font-medium text-indigo-500">Two-Factor Authentication</h3>
              <p className="text-sm text-indigo-400">Add an extra layer of security</p>
            </div>
            <Badge className="bg-slate-400 text-indigo-400">Coming Soon</Badge>
          </div>
          
          {user?.auth_method === "email" && (
            <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl border border-slate-700">
              <div>
                <h3 className="font-medium text-indigo-500">Change Password</h3>
                <p className="text-sm text-indigo-400">Update your account password</p>
              </div>
              <Button variant="outline" size="sm" className="border-slate-600 text-slate-400">
                <Key className="w-4 h-4 mr-2" />
                Change
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="bg-slate-800/50 border-red-500/30">
        <CardHeader>
          <CardTitle className="text-red-400 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-red-500/10 rounded-xl border border-red-500/30">
            <div>
              <h3 className="font-medium text-indigo-500">Delete Account</h3>
              <p className="text-sm text-indigo-400">Permanently delete your account and all data</p>
            </div>
            <Button variant="outline" size="sm" className="border-red-500/50 text-red-400 hover:bg-red-500/20">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Disconnect Gmail Dialog */}
      <Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-indigo-500">Disconnect Gmail?</DialogTitle>
            <DialogDescription className="text-indigo-400">
              Client emails will be sent from our system address (hello@yourfreelanceros.com) with your email as reply-to. You can reconnect Gmail anytime.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisconnectDialog(false)} className="border-slate-600 text-slate-400">
              Cancel
            </Button>
            <Button 
              onClick={disconnectGmail}
              disabled={disconnecting}
              className="bg-red-500 hover:bg-red-600"
            >
              {disconnecting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <LogOut className="w-4 h-4 mr-2" />}
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
